// src/app/services/[service]/components/api/ApiIntegrations.jsx
"use client";

import React from "react";
import Image from "next/image";
import styles from "./ApiIntegrations.module.css";

export default function ApiIntegrations({ dynamicData }) {
  // If no dynamic data, use the original static layout
  if (!dynamicData) {
    return (
      <section className={`${styles.section} py-5`}>
        <div className="container">
          <div className={styles.head} data-reveal="fade">
            <div className={styles.eyebrow}>INTEGRATIONS</div>
            <h2 className={styles.title}>
              Extend Your Platform with Third‑Party APIs
            </h2>
            <p className={styles.subtitle}>
              We integrate payments, shipping, communication, and social platforms
              to deliver secure, scalable functionality tailored to your stack.
            </p>
          </div>

          <div className={styles.grid} data-reveal="stagger-mix">
            {/* Payment Gateways APIs */}
            <div className={styles.card}>
              <h3 className={styles.ctitle}>Payment Gateways APIs</h3>
              <p className={styles.cdesc}>
                We integrate top payment gateway APIs, including PayPal,
                Authorize.Net, and PayU, with your e‑commerce sites, websites, and
                social networks, ensuring fast and efficient payment processing.
              </p>
              <div className={styles.brands}>
                <Image src="/assets/img/apidev/paypal.svg" alt="PayPal" className={styles.brand} width={100} height={40} style={{ objectFit: "contain" }} />
                <Image src="/assets/img/apidev/authorizenet.webp" alt="Authorize.Net" className={styles.brand} width={100} height={40} style={{ objectFit: "contain" }} />
                <Image src="/assets/img/apidev/payu.webp" alt="PayU" className={styles.brand} width={100} height={40} style={{ objectFit: "contain" }} />
              </div>
            </div>

            {/* Shipping Carriers APIs */}
            <div className={styles.card}>
              <h3 className={styles.ctitle}>Shipping Carriers APIs</h3>
              <p className={styles.cdesc}>
                Our services include seamless integration with leading shipping
                carrier APIs such as FedEx, UPS, and USPS, tailored to meet your
                custom shipping needs.
              </p>
              <div className={styles.brands}>
                <Image src="/assets/img/apidev/fedex.webp" alt="FedEx" className={styles.brand} width={100} height={40} style={{ objectFit: "contain" }} />
                <Image src="/assets/img/apidev/ups.svg" alt="UPS" className={styles.brand} width={100} height={40} style={{ objectFit: "contain" }} />
                <Image src="/assets/img/apidev/usps.svg" alt="USPS" className={styles.brand} width={100} height={40} style={{ objectFit: "contain" }} />
              </div>
            </div>

            {/* SMS & Communication APIs */}
            <div className={styles.card}>
              <h3 className={styles.ctitle}>SMS & Communication APIs</h3>
              <p className={styles.cdesc}>
                We connect your business systems with popular communication
                platforms like Twilio, Plivo, and Nexmo, enhancing communication
                through effective web service API integrations.
              </p>
              <div className={styles.brands}>
                <Image src="/assets/img/apidev/twilio.webp" alt="Twilio" className={styles.brand} width={100} height={40} style={{ objectFit: "contain" }} />
                <Image src="/assets/img/apidev/nexmo.webp" alt="Nexmo" className={styles.brand} width={100} height={40} style={{ objectFit: "contain" }} />
                <Image src="/assets/img/apidev/plivo.webp" alt="Plivo" className={styles.brand} width={100} height={40} style={{ objectFit: "contain" }} />
              </div>
            </div>

            {/* Social Network APIs */}
            <div className={styles.card}>
              <h3 className={styles.ctitle}>Social Network APIs</h3>
              <p className={styles.cdesc}>
                Link your social media platforms such as YouTube, Facebook, and
                Instagram with your website, e‑commerce store, and analytics
                systems using our API integration solutions.
              </p>
              <div className={styles.brands}>
                <Image src="/assets/img/apidev/instagram.webp" alt="Instagram" className={styles.brand} width={100} height={40} style={{ objectFit: "contain" }} />
                <Image src="/assets/img/apidev/youtube.webp" alt="YouTube" className={styles.brand} width={100} height={40} style={{ objectFit: "contain" }} />
                <Image src="/assets/img/apidev/facebook.svg" alt="Facebook" className={styles.brand} width={100} height={40} style={{ objectFit: "contain" }} />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Dynamic Content Render
  const { tag, title, subtitle, integrations = [] } = dynamicData;

  return (
    <section className={`${styles.section} py-5`}>
      <div className="container">
        <div className={styles.head} data-reveal="fade">
          <div className={styles.eyebrow}>{tag || "INTEGRATIONS"}</div>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        <div className={styles.grid} data-reveal="stagger-mix">
          {integrations.map((item, idx) => (
            <div key={idx} className={styles.card}>
              <div className="d-flex align-items-center gap-3 mb-3">
                {item.icon && (
                  <div className={styles.dynamicIcon}>
                    <i className={item.icon}></i>
                  </div>
                )}
                <h3 className={`${styles.ctitle} mb-0`}>{item.name}</h3>
              </div>
              <p className={styles.cdesc}>{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
